import { apiClient } from "@/api/apiClient";
import type { DistanceUnit } from "@/models/siteUnit.types";
import {
	DEFAULT_LAYER_MODE,
	type ICompactionOutline,
	type ISpreadingCompaction,
	type ISpreadingCompactionDraft,
	type ISpreadingCompactionInput,
	type ITarget,
	type ITerrain,
	type TLayerMode,
	type TOffsetType,
	layerModes,
} from "@/store/modules/spreading-compaction-types";
import { AssetType, type EarthBrainViewer } from "earthbrain-viewer";
import {
	type SpreadingCompactionApiJobState,
	type SpreadingCompactionApiListResponse,
	type SpreadingCompactionApiRecord,
	type SpreadingCompactionApiRequest,
	mergeApiSurfacesToInput,
	toStoreCompactionOutline,
} from "./apiTypes";
import {
	buildSurfaceExportFiles,
	sanitizeSurfaceExportName,
} from "./exporters/surfaceExportFiles";
import { buildAndSaveZip } from "./exporters/zipExporter";
import { buildOffsetInitialVisibility } from "./spreadingCompactionHelpers";
import { normalizeDistanceUnit } from "./unitConverters";

type IGeometry = ISpreadingCompactionInput["surfaces"][number]["geometry"];

type TRootState = Record<string, unknown>;

type TFormSource = ISpreadingCompaction | ISpreadingCompactionDraft;

// --- API client ---

function scResource(_siteUuid: string) {
	return apiClient.v2.api[":siteId"]["spreading-compaction"];
}

interface IFaceSelectionHandlers {
	onStart: () => void;
	onProgress: (
		selected: ReadonlySet<number>,
		seeds: ReadonlySet<number>,
		excluded: ReadonlySet<number>,
	) => void;
}

function getViewer(rootState: TRootState): EarthBrainViewer {
	return rootState.viewer as EarthBrainViewer;
}

function getSiteUuid(rootState: TRootState): string | null {
	const site = rootState.site as { site_uuid?: string } | null | undefined;
	return site?.site_uuid ?? null;
}

function getSiteName(rootState: TRootState): string {
	const site = rootState.site as { site_name?: string } | null | undefined;
	return site?.site_name ?? "";
}

/**
 * 出力ファイル名の接頭辞 `<現場名>_<SpreadingCompaction名>`。
 * 両者を個別にサニタイズしてから繋ぐ (区切りの `_` を現場名側の記号に食われないようにする)。
 */
export function buildExportDisplayName(
	siteName: string,
	spreadingCompactionName: string,
): string {
	return `${sanitizeSurfaceExportName(siteName)}_${sanitizeSurfaceExportName(spreadingCompactionName)}`;
}

/** Site distance unit; falls back to metric while the site is not loaded yet. */
function getSiteDistanceUnit(rootState: TRootState): DistanceUnit {
	const site = rootState.site as
		| { units?: { distance?: unknown } }
		| null
		| undefined;
	return normalizeDistanceUnit(site?.units?.distance);
}

function getTargetGeometry(
	viewer: EarthBrainViewer,
	target: ITarget,
): IGeometry | undefined {
	switch (target.type) {
		case "road":
			return viewer.getRoadMeshGeometry(target.id);
		case "flatground":
			return viewer.getFlatGroundMeshGeometry(target.id);
		case "design":
			return viewer.getDesignMeshGeometry(target.id);
	}
}

// --- Terrain extension (ADR 0003) ---

/** Skirt subdivision pitch (m), shared by every target type. */
const TERRAIN_EXTENSION_PITCH = 5;

type TPointCloudList = ReadonlyArray<{
	point_cloud_id: number;
	asset_id?: string;
}>;

function getPointCloudList(rootState: TRootState): TPointCloudList {
	return (rootState.point_cloud_list ?? []) as TPointCloudList;
}

/**
 * Viewer keys point clouds by asset_id (see `addPointCloudTileset`), while a
 * SpreadingCompaction terrain is keyed by point_cloud_id.
 * @returns undefined when the point cloud is unknown or has no asset_id
 */
function resolveTerrainViewerId(
	rootState: TRootState,
	terrain: Readonly<ITerrain>,
): number | undefined {
	const assetId = getPointCloudList(rootState).find(
		(pc) => pc.point_cloud_id === terrain.id,
	)?.asset_id;
	if (assetId === undefined) return undefined;
	const parsed = Number.parseInt(assetId, 10);
	return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * Target geometry as sent in `base.geometry`: the mesh extended vertically down
 * to the selected terrain. Applied to every target — a mesh already draped on
 * that terrain has nothing to drop, so no per-target decision is needed here.
 * @returns undefined when the target mesh is not available
 */
export function getRequestGeometry(
	viewer: EarthBrainViewer,
	rootState: TRootState,
	target: Readonly<ITarget>,
	terrain: Readonly<ITerrain>,
): IGeometry | undefined {
	const geometry = getTargetGeometry(viewer, target);
	if (!geometry) return undefined;
	const viewerTerrainId = resolveTerrainViewerId(rootState, terrain);
	if (viewerTerrainId === undefined) {
		throw new Error(`Terrain asset not found: pointcloud:${terrain.id}`);
	}
	return viewer.extendGeometryToTerrain(
		geometry,
		viewerTerrainId,
		AssetType.Point_Cloud,
		TERRAIN_EXTENSION_PITCH,
	);
}

// --- Viewer state ---

function viewerKey(id: number): string {
	return String(id);
}

export function teardownViewerState(
	spreadingCompaction: ISpreadingCompaction,
	rootState: TRootState,
): void {
	try {
		const viewer = getViewer(rootState);
		if (viewer.isFaceSelectionActive()) {
			viewer.stopFaceSelection();
		}
		viewer.setSpreadingCompactionVisible(
			viewerKey(spreadingCompaction.id),
			false,
		);
	} catch (error: unknown) {
		console.warn("[spreading-compaction] teardown failed:", error);
	}
}

/**
 * Sync viewer entity with the store entry: create the viewer SC if missing,
 * then push current visibility flags. Idempotent — safe to call repeatedly.
 */
export async function syncViewerWithEntry(
	spreadingCompaction: ISpreadingCompaction | undefined,
	rootState: TRootState,
): Promise<void> {
	if (spreadingCompaction?.execution.status !== "success") return;

	try {
		const viewer = getViewer(rootState);
		const key = viewerKey(spreadingCompaction.id);
		if (!viewer.hasSpreadingCompaction(key)) {
			// Must await — visibility setters below throw if SC not yet registered.
			await viewer.createSpreadingCompaction(
				key,
				spreadingCompaction.execution.result,
			);
		}

		viewer.setSpreadingCompactionVisible(key, spreadingCompaction.visible);

		for (const offsetVis of spreadingCompaction.offsetVisibility) {
			viewer.setSpreadingCompactionSurfaceVisible(
				key,
				offsetVis.type,
				offsetVis.offset,
				offsetVis.visible,
			);
		}
	} catch (error: unknown) {
		console.warn("[spreading-compaction] sync viewer failed:", error);
	}
}

// --- Execution ---

export type StartSpreadingCompactionResult = {
	/** Row the result lands in; for a new request the server creates it up front. */
	id: number;
};

/**
 * POST → 202. Starts a calculation and returns immediately; use
 * `trackSpreadingCompactionJob` to follow it to completion. No viewer
 * side-effects. Pass `upsertId` to re-execute an existing row (409 while
 * it is still running).
 */
export async function startSpreadingCompactionApi(
	source: TFormSource & { target: ITarget; terrain: ITerrain },
	upsertId: number | undefined,
	rootState: TRootState,
): Promise<StartSpreadingCompactionResult> {
	const viewer = getViewer(rootState);
	const siteUuid = getSiteUuid(rootState);
	if (!siteUuid) throw new Error("Site not found");

	const geometry = getRequestGeometry(
		viewer,
		rootState,
		source.target,
		source.terrain,
	);
	if (!geometry) throw new Error("Target geometry not found");

	const selectedFaces = await viewer.getSelectedFaceLocalGeometry();
	const body = buildRequestBody(
		source,
		upsertId,
		geometry,
		// In horizontal mode the viewer already returns the horizontal base plane (ADR 0006).
		selectedFaces,
	);

	const res = await scResource(siteUuid).$post({
		param: { siteId: siteUuid },
		json: body,
	});
	if (res.status === 409 && upsertId !== undefined) {
		throw new SpreadingCompactionAlreadyRunningError(upsertId);
	}
	if (!res.ok) {
		throw new Error(`API error: ${res.status} ${res.statusText}`);
	}
	const accepted: SpreadingCompactionApiRecord = await res.json();
	return { id: accepted.id };
}

/**
 * Re-execute refused because a run already holds the row (started elsewhere,
 * e.g. another tab). The row is genuinely processing — follow it, don't error.
 */
export class SpreadingCompactionAlreadyRunningError extends Error {
	override readonly name = "SpreadingCompactionAlreadyRunningError";
	constructor(readonly id: number) {
		super("Spreading compaction is already running");
	}
}

// --- Job polling ---

/** First poll delay (ms); grows by POLL_BACKOFF up to POLL_MAX_INTERVAL_MS. */
const POLL_INITIAL_INTERVAL_MS = 2_000;
const POLL_MAX_INTERVAL_MS = 5_000;
const POLL_BACKOFF = 1.5;
/**
 * Consecutive poll failures tolerated before giving up. The job keeps running
 * on the server, so a transient 5xx / network blip must not flip the entry
 * to error. 404 is final (row deleted, or unknown to this site).
 */
const POLL_MAX_CONSECUTIVE_FAILURES = 3;

/** Injectable for tests. */
export type TSleep = (ms: number) => Promise<void>;
const defaultSleep: TSleep = (ms) =>
	new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Polls `GET /spreading-compaction/:id` until `job.status` is terminal.
 * Resolves with the record (surfaces inline) on `succeeded`, throws with
 * the server-side message on `failed`.
 */
export async function pollSpreadingCompactionJob(
	siteUuid: string,
	id: number,
	sleep: TSleep = defaultSleep,
): Promise<SpreadingCompactionApiRecord> {
	let interval = POLL_INITIAL_INTERVAL_MS;
	let failures = 0;
	for (;;) {
		await sleep(interval);
		interval = Math.min(interval * POLL_BACKOFF, POLL_MAX_INTERVAL_MS);

		let record: SpreadingCompactionApiRecord;
		try {
			const res = await scResource(siteUuid)[":id"].$get({
				param: { siteId: siteUuid, id: String(id) },
			});
			if (res.status === 404) {
				throw new PollFinalError(`API error: ${res.status} ${res.statusText}`);
			}
			if (!res.ok) {
				throw new Error(`API error: ${res.status} ${res.statusText}`);
			}
			record = await res.json();
			failures = 0;
		} catch (error: unknown) {
			if (error instanceof PollFinalError) throw new Error(error.message);
			failures += 1;
			if (failures >= POLL_MAX_CONSECUTIVE_FAILURES) throw error;
			console.warn("[spreading-compaction] poll failed, retrying:", error);
			continue;
		}
		switch (record.job.status) {
			case "running":
				continue;
			case "succeeded":
				return record;
			case "failed":
				throw new Error(record.job.error);
		}
	}
}

/** Poll outcome that must not be retried. */
class PollFinalError extends Error {
	override readonly name = "PollFinalError";
}

/** Store access needed to reflect a job outcome; satisfied by both the Vuex action ctx and the composable. */
export interface IJobTrackerStore {
	commit: (type: string, payload?: unknown) => void;
	getEntry: (id: number) => ISpreadingCompaction | undefined;
	rootState: TRootState;
}

/**
 * Follows the run of entry `id` to completion and reflects the outcome on
 * the store entry: success → surfaces + offset visibility + viewer sync;
 * failure → error state. Never throws (failures are recorded on the entry).
 * Shared by execute (fresh run) and `loadAndRestore` (resume after reload).
 */
export async function trackSpreadingCompactionJob(
	id: number,
	store: IJobTrackerStore,
): Promise<void> {
	const siteUuid = getSiteUuid(store.rootState);
	if (!siteUuid) {
		store.commit("SET_EXECUTION", {
			id,
			execution: { status: "error", error: "Site not found" },
		});
		return;
	}
	try {
		const result = await pollSpreadingCompactionJob(siteUuid, id);
		// No site guard needed: ids are a global PK, so after a site switch the
		// entry is simply absent and the commits below are no-ops.
		const input = mergeApiSurfacesToInput(result.spreading, result.compaction);
		store.commit("SET_EXECUTION", {
			id,
			execution: {
				status: "success",
				result: input,
				compactionOutline: toStoreCompactionOutline(result.compactionOutline),
			},
		});
		for (const offsetVis of buildOffsetInitialVisibility(input.surfaces)) {
			store.commit("SET_OFFSET_VISIBLE", { id, ...offsetVis });
		}
		// Re-execute: dispose the old viewer entity, then sync from the updated entry.
		disposeViewerEntry(id, store.rootState);
		const updated = store.getEntry(id);
		if (updated) await syncViewerWithEntry(updated, store.rootState);
		await refreshCrossSection(store.rootState);
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : "Unknown error";
		store.commit("SET_EXECUTION", {
			id,
			execution: { status: "error", error: message },
		});
		console.error("[spreading-compaction] job failed:", error);
	}
}

/**
 * Disposes the viewer entity for an old id (used when an upsert response
 * returns a different id, or when re-executing an existing entry).
 */
export function disposeViewerEntry(id: number, rootState: TRootState): void {
	const viewer = getViewer(rootState);
	if (viewer.hasSpreadingCompaction(viewerKey(id))) {
		viewer.clearSpreadingCompaction(viewerKey(id));
	}
}

// --- List + Delete ---

export async function loadAllSpreadingCompactions(
	rootState: TRootState,
): Promise<ISpreadingCompaction[]> {
	const siteUuid = getSiteUuid(rootState);
	if (!siteUuid) return [];

	const res = await scResource(siteUuid).$get({
		param: { siteId: siteUuid },
	});
	if (!res.ok) {
		throw new Error(`API error: ${res.status} ${res.statusText}`);
	}
	const data: SpreadingCompactionApiListResponse = await res.json();
	const out: ISpreadingCompaction[] = [];
	for (const item of data.items) {
		const entity = toStoreEntity(item, rootState);
		if (entity) out.push(entity);
	}
	return out;
}

export async function deleteSpreadingCompactionApi(
	id: number,
	rootState: TRootState,
): Promise<void> {
	const siteUuid = getSiteUuid(rootState);
	if (!siteUuid) return;
	const res = await apiClient.v2.api[":siteId"]["spreading-compaction"][
		":id"
	].$delete({
		param: { siteId: siteUuid, id: String(id) },
	});
	if (!res.ok && res.status !== 404) {
		throw new Error(`API error: ${res.status} ${res.statusText}`);
	}
}

function resolveTargetName(
	rootState: TRootState,
	type: ITarget["type"],
	id: ITarget["id"],
): string {
	if (type === "design") {
		const designs = (rootState.design_list ?? []) as Array<{
			asset_id: string;
			design_name: string;
		}>;
		return designs.find((d) => d.asset_id === String(id))?.design_name ?? "";
	}
	const objectModule = rootState.object as
		| { objects?: Array<{ id: string | number; type: string; name?: string }> }
		| undefined;
	const objects = objectModule?.objects ?? [];
	const objType = type === "flatground" ? "flat_ground" : type;
	return (
		objects.find((o) => o.type === objType && String(o.id) === String(id))
			?.name ?? ""
	);
}

function resolveTerrainName(rootState: TRootState, id: ITerrain["id"]): string {
	const list = (rootState.point_cloud_list ?? []) as Array<{
		point_cloud_id: number;
		point_cloud_name?: string;
	}>;
	return list.find((p) => p.point_cloud_id === id)?.point_cloud_name ?? "";
}

function toStoreEntity(
	item: SpreadingCompactionApiRecord,
	rootState: TRootState,
): ISpreadingCompaction | null {
	const input = mergeApiSurfacesToInput(item.spreading, item.compaction);
	let target: ITarget;
	if (item.target.type === "design") {
		const designId = Number.parseInt(item.target.id, 10);
		if (!Number.isSafeInteger(designId)) {
			console.warn(
				`[spreading-compaction] invalid design target id="${item.target.id}" — skipping record id=${item.id}`,
			);
			return null;
		}
		target = {
			type: "design",
			id: designId,
			name: resolveTargetName(rootState, "design", designId),
		};
	} else {
		target = {
			type: item.target.type,
			id: item.target.id,
			name: resolveTargetName(rootState, item.target.type, item.target.id),
		};
	}
	const terrain: ITerrain | null = item.terrain
		? {
				type: "pointcloud",
				id: item.terrain.id,
				name: resolveTerrainName(rootState, item.terrain.id),
			}
		: null;
	return {
		id: item.id,
		name: item.name,
		target,
		terrain,
		compactionSurfaceSpacing: item.compactionSurfaceSpacing,
		slopeShoulderOffset: item.slopeShoulderOffset,
		// Not persisted server-side: the mode only shapes the base sent at execute time.
		layerMode: DEFAULT_LAYER_MODE,
		execution: toExecution(
			item.job,
			input,
			toStoreCompactionOutline(item.compactionOutline),
		),
		// Hidden on load — user must click an item to display it.
		visible: false,
		targetVisible: true,
		targetOpacity: 0.7,
		offsetVisibility: buildOffsetInitialVisibility(input.surfaces),
	};
}

/** Server run state → store execution state. `running` entries are polled by `loadAndRestore`. */
function toExecution(
	job: SpreadingCompactionApiJobState,
	input: ISpreadingCompactionInput,
	compactionOutline: ReadonlyArray<ICompactionOutline>,
): ISpreadingCompaction["execution"] {
	switch (job.status) {
		case "running":
			return { status: "processing" };
		case "failed":
			return { status: "error", error: job.error };
		case "succeeded":
			return { status: "success", result: input, compactionOutline };
	}
}

export function clearSpreadingCompactionResult(
	id: number,
	rootState: TRootState,
): void {
	getViewer(rootState).clearSpreadingCompaction(viewerKey(id));
}

export function setAllResultsVisible(
	id: number,
	rootState: TRootState,
	visible: boolean,
): void {
	try {
		getViewer(rootState).setSpreadingCompactionVisible(viewerKey(id), visible);
	} catch (e: unknown) {
		console.warn("[spreading-compaction] setAllResultsVisible failed:", e);
	}
}

export async function refreshCrossSection(
	rootState: TRootState,
): Promise<void> {
	try {
		await getViewer(rootState).refreshSpreadingCompactionCrossSection();
	} catch {
		// cross-section not active
	}
}

// --- Tab ---

/**
 * Leaving the tab closes the edit panel (`isActive` = panel open, not tab
 * shown) and stops face selection. Returning does nothing: the list is shown
 * and the panel reopens only when the user selects an entry or adds one —
 * a still-selected entry (list highlight / viewer visibility) must not
 * reopen it by itself.
 */
export function handleSpreadingCompactionTabActiveChange(
	rootState: TRootState,
	isTabActive: boolean,
	closeEditPanel: () => void,
): void {
	if (isTabActive) return;
	try {
		const viewer = getViewer(rootState);
		if (viewer.isFaceSelectionActive()) {
			viewer.stopFaceSelection();
		}
	} catch {
		// viewer not available
	}
	closeEditPanel();
}

// --- Face selection ---

export async function startFaceSelection(
	source: TFormSource,
	rootState: TRootState,
	handlers: IFaceSelectionHandlers,
): Promise<void> {
	if (!source.target) return;

	const viewer = getViewer(rootState);
	const localGeometry = getTargetGeometry(viewer, source.target);
	if (!localGeometry || localGeometry.vertices.length === 0) return;

	await viewer.startFaceSelection(localGeometry, handlers);
}

export function stopFaceSelection(rootState: TRootState): void {
	getViewer(rootState).stopFaceSelection();
}

export function setFaceSelectionAngleTolerance(
	rootState: TRootState,
	degrees: number,
): void {
	getViewer(rootState).setFaceSelectionAngleTolerance(degrees);
}

/**
 * レイヤモード → viewer の面選択基準面モード (ADR 0006)。
 * 水平のとき viewer は選択面の最高 z × 対象 AABB の水平面を描き、`getSelectedFaceLocalGeometry`
 * もその面を返すので、client はモードを伝えるだけでよい。
 */
export function applyLayerModeToFaceSelection(
	rootState: TRootState,
	layerMode: TLayerMode,
): void {
	getViewer(rootState).setFaceSelectionBaseMode(
		layerMode === layerModes.HORIZONTAL ? "horizontal" : "faces",
	);
}

// --- Viewer updates ---

export function updateViewerTargetOpacity(
	id: number,
	rootState: TRootState,
	opacity: number,
): void {
	getViewer(rootState).setSpreadingCompactionOpacity(viewerKey(id), opacity);
}

export function updateViewerOffsetVisible(
	id: number,
	rootState: TRootState,
	payload: { type: TOffsetType; offset: number; visible: boolean },
): void {
	getViewer(rootState).setSpreadingCompactionSurfaceVisible(
		viewerKey(id),
		payload.type,
		payload.offset,
		payload.visible,
	);
}

// --- Export ---

export async function exportSpreadingCompaction(
	spreadingCompaction: ISpreadingCompaction,
	rootState: TRootState,
): Promise<void> {
	if (spreadingCompaction.execution.status !== "success") return;

	const geometries = getViewer(rootState).getSpreadingCompactionGeometries(
		viewerKey(spreadingCompaction.id),
	);
	if (geometries.length === 0) return;

	const siteUuid = getSiteUuid(rootState);
	if (!siteUuid) throw new Error("Site is not available");

	await buildAndSaveZip({
		displayName: buildExportDisplayName(
			getSiteName(rootState),
			spreadingCompaction.name,
		),
		parts: [...geometries],
		siteId: siteUuid,
		distanceUnit: getSiteDistanceUnit(rootState),
		compactionOutline: spreadingCompaction.execution.compactionOutline,
	});
}

/** Saves spreading LandXML and compaction CSV files through v2-server. */
export async function exportSpreadingCompactionToGroupware(
	spreadingCompaction: ISpreadingCompaction,
	rootState: TRootState,
): Promise<void> {
	if (spreadingCompaction.execution.status !== "success") return;

	const siteUuid = getSiteUuid(rootState);
	if (!siteUuid) throw new Error("Site is not available");
	const geometries = getViewer(rootState).getSpreadingCompactionGeometries(
		viewerKey(spreadingCompaction.id),
	);
	if (geometries.length === 0) return;
	const files = buildSurfaceExportFiles(
		buildExportDisplayName(getSiteName(rootState), spreadingCompaction.name),
		[...geometries],
		getSiteDistanceUnit(rootState),
		spreadingCompaction.execution.compactionOutline,
	);

	const res = await apiClient.v2.api[":siteId"]["spreading-compaction"][
		":id"
	].groupware.$post({
		param: { siteId: siteUuid, id: String(spreadingCompaction.id) },
		json: { files },
	});
	if (!res.ok) {
		throw new Error(`API error: ${res.status} ${res.statusText}`);
	}
}

// --- Private helpers ---

function buildRequestBody(
	source: TFormSource & { target: ITarget; terrain: ITerrain },
	upsertId: number | undefined,
	geometry: IGeometry,
	topEdgesGeometry: IGeometry | null,
): SpreadingCompactionApiRequest {
	const body: SpreadingCompactionApiRequest = {
		name: source.name,
		target: {
			type: source.target.type,
			id: String(source.target.id),
		},
		terrain: {
			type: source.terrain.type,
			id: source.terrain.id,
		},
		base: {
			geometry: {
				vertices: [...geometry.vertices],
				indices: [...geometry.indices],
			},
		},
		spreadingConfig: {
			topEdgesOfBaseSlope: topEdgesGeometry ?? { vertices: [], indices: [] },
		},
		compactionConfig: {
			slopeShoulderOffset: source.slopeShoulderOffset,
			compactionSurfaceSpacing: source.compactionSurfaceSpacing,
		},
	};

	if (upsertId !== undefined) {
		body.id = upsertId;
	}

	return body;
}
